import React, { Fragment, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import { Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, InputText, Menu, MenuItem, NotificationUtils, Select, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { FormikErrors, FormikProps } from "formik"
import { v4 as uuidv4 } from "uuid"

import { DataBlock } from "../../../features/Ruleset/components/RuleCard"
import { ArrowAdornment } from "../../../features/Scorecards/components/Condition"
import type { ConstantFeature, SchemaFeature } from "../../../features/Workflows"
import { useDebounce } from "../../../hooks/useDebounce"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { Label } from "../../../types/generated/api/Label"
import type { PaginatedLabelList } from "../../../types/generated/api/PaginatedLabelList"
import { WorkflowConstantFeature } from "../../../types/generated/api/WorkflowConstantFeature"
import { WorkflowSchemaFeatureRequest } from "../../../types/generated/api/WorkflowSchemaFeatureRequest"
import { PROHIBITED_LABELS_KEYWORDS } from "../../../utils/constants"
import { extractPageFromBackEndPaginationLink, isInputValueValid } from "../../../utils/genericHelpers"
import { LabelsMenu } from "../../form/LabelsMenu"
import type { Rule } from "../DecisionNodeDialog"

import styles from "./Condition.module.scss"

type Props = {
  readMode: boolean
  ruleInReadMode: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workflowFormik: FormikProps<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any
  index: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any
  disableRule: boolean
  mode: { create: boolean; edit: boolean }
  handleRemoveRule: () => void
}

export function Condition({
  readMode,
  ruleInReadMode,
  index,
  item,
  disableRule,
  handleRemoveRule,
  mode,
  formik,
  workflowFormik,
}: Readonly<Props>): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()
  const { t } = useTranslation(["dialogs", "common"])

  const [filteredFeatures, setFilteredFeatures] = useState<Array<{ uuid: string; name: string }>>([])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [type, setType] = useState<string>("TEXT")

  const errorFormikRules = (formik?.errors?.ruleset as FormikErrors<{ rules: Rule[] }> | undefined)?.rules
  const touchedFormikRules =
    formik?.touched?.ruleset && typeof formik.touched.ruleset === "object" && "rules" in formik.touched.ruleset
      ? formik.touched.ruleset.rules
      : undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputRef = useRef<any>()

  const pageSize = 20
  const [page, setPage] = useState<number>(0)
  const [searchKey, setSearchKey] = useState<string | null>(formik?.values.ruleset.rules[index]?.returnValue ?? null)

  const [labels, setLabels] = useState<Label[]>([])

  const CreateLabelMutation = useMutation<
    AxiosResponse<Label>,
    AxiosError,
    {
      project_uuid: string
      name: string
    }
  >(KonanAPI.CreateLabel, {
    onSuccess: async ({ data }) => {
      NotificationUtils.toast(t("Condition.messages.labelCreated"), { severity: "success" })

      formik.setFieldValue(`ruleset.rules.${[index]}.label_id`, data.uuid)
      formik.setFieldValue(`ruleset.rules.${[index]}.returnValue`, data.name)

      // Add the new label to the labels array
      setLabels((prevLabels) => [...prevLabels, data])

      await queryClient.invalidateQueries("label-list")
    },
    onError: () => {
      NotificationUtils.toast(t("Condition.messages.labelCreationFailed"), { severity: "error" })
    },
  })

  // fetch project labels
  const {
    isLoading: isLabelListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedLabelList>, AxiosError, Label>(
    ["label-list", projectId, page, pageSize, searchKey],
    ({ pageParam = 1 }) =>
      KonanAPI.RetrieveLabels({
        project_uuid: projectId as string,
        pageSize: pageSize,
        page: pageParam,
        search: searchKey,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<Label[]>((accumulator, page) => accumulator.concat(page.data.results as Label[]), []),
      }),
      onSuccess: (response) => {
        // filtering out already existing labels in the list
        const tempLabels =
          response.pages?.filter((item: Label) => !labels.find((label: Label) => label.uuid === item.uuid)) ?? []

        setLabels([...labels, ...tempLabels])

        !["", null, undefined].includes(searchKey) &&
          // setting name (automatically) ONLY if the user writes the full name correctly
          response.pages[0]?.name === searchKey &&
          formik.setFieldValue(`cards[${index}].label_id`, response.pages[0]?.uuid)
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !ruleInReadMode,
      refetchOnMount: true,
    },
  )

  // fetch label if first mount
  const { isLoading: isSingleLabelLoading } = useQuery<AxiosResponse<Label>, AxiosError>(
    ["label", formik?.values.ruleset.rules[index]?.label_id],
    () =>
      KonanAPI.FetchLabel({
        project_uuid: projectId as string,
        label_uuid: formik?.values.ruleset.rules[index]?.label_id,
      }),
    {
      onSuccess: (response) => {
        formik.setFieldValue(`ruleset.rules.${[index]}.returnValue`, response.data.name)
      },
      enabled: ruleInReadMode,
      refetchOnMount: true,
    },
  )

  const adjustedFeatures = useMemo(() => {
    return [
      ...(workflowFormik?.values?.features
        ?.filter((item: SchemaFeature) => item.type === WorkflowSchemaFeatureRequest.type.NUMBER)
        .map((item: SchemaFeature) => {
          return { name: item.name, uuid: item.id }
        }) || []),
      ...(workflowFormik?.values?.computedFeatures?.map((item: { uuid: string; value: string }) => {
        return { name: item.value, uuid: item.uuid }
      }) || []),
      ...(workflowFormik?.values?.constants
        ?.filter((item: ConstantFeature) => item.type === WorkflowConstantFeature.type.NUMBER)
        .map((item: ConstantFeature) => {
          return { name: item.name, uuid: item.id }
        }) || []),
    ]
  }, [workflowFormik?.values?.features, workflowFormik?.values?.computedFeatures, workflowFormik?.values?.constants])

  const debouncedOnChange = useDebounce((): void => {
    const label = formik?.values.ruleset.rules[index]?.returnValue ?? ""

    setPage(0)
    setSearchKey(label === "" ? null : label)
  }, 400)

  const debouncedFilterChange = useDebounce((): void => {
    setFilteredFeatures(
      adjustedFeatures?.filter((item: { uuid: string; name: string }) =>
        item.name.includes(formik.values.ruleset.rules[index]?.feature),
      ),
    )
    setAnchorEl(inputRef?.current)
    setTimeout(() => {
      inputRef?.current?.focus()
    }, 100)
  }, 400)

  const handleAddNewValue = async (): Promise<void> => {
    const labelValue = formik?.values.ruleset.rules[index]?.returnValue

    if (isInputValueValid("label", labelValue, PROHIBITED_LABELS_KEYWORDS)) {
      await CreateLabelMutation.mutateAsync({
        project_uuid: projectId as string,
        name: labelValue as string,
      }).then((res) => {
        formik.setFieldValue(`ruleset.rules.${[index]}.returnValue`, res.data.name)
        formik.setFieldValue(`ruleset.rules.${[index]}.label_id`, res.data.uuid)
      })
    } else {
      formik.setFieldValue(`ruleset.rules.${[index]}.returnValue`, labelValue)
    }
  }

  useEffect(() => {
    if (adjustedFeatures?.length > 0) {
      setFilteredFeatures(adjustedFeatures)
    }
  }, [adjustedFeatures])

  useEffect(() => {
    if (adjustedFeatures && inputRef?.current && workflowFormik?.values?.features?.length > 0) {
      inputRef?.current?.focus()
    }
  }, [inputRef, adjustedFeatures, workflowFormik?.values?.features])

  return (
    <Grid container item className={styles.conditionContainer} spacing={1} key={item.id}>
      {/* Feature */}
      <Grid item xs={3} mt={readMode ? 1 : 0}>
        {ruleInReadMode ? (
          <DataBlock value={formik?.values.ruleset.rules[index]?.feature} />
        ) : (
          <Fragment>
            <div>
              <InputText
                anchoringRef={inputRef}
                id={`ruleset.rules[${index}].feature`}
                placeholder={t("Condition.placeholders.feature")}
                key={formik?.values.ruleset.rules[index]?.id}
                value={formik?.values.ruleset.rules[index]?.feature}
                handleChange={(e: InputChangeEvent) => {
                  formik?.handleChange(e)
                  debouncedFilterChange()
                }}
                hideDescription
                endAdornment={
                  adjustedFeatures.length > 0 ? (
                    <ArrowAdornment
                      anchorEl={anchorEl}
                      onClick={() => setAnchorEl(!!anchorEl ? null : inputRef?.current)}
                    />
                  ) : undefined
                }
                // error={
                //   touchedFormikRules &&
                //   errorFormikRules &&
                //   touchedFormikRules[index]?.feature &&
                //   (errorFormikRules[index] as FormikErrors<Rule>)?.feature &&
                //   Boolean((errorFormikRules[index] as FormikErrors<Rule>)?.feature) &&
                //   (errorFormikRules[index] as FormikErrors<Rule>)?.feature
                // }
                handleBlur={formik?.handleBlur}
                disabled={formik?.isSubmitting}
                fullWidth
              />
            </div>
            {(workflowFormik?.values?.features?.filter(
              (item: SchemaFeature) => item.type === WorkflowSchemaFeatureRequest.type.NUMBER,
            )?.length > 0 ||
              workflowFormik?.values?.computedFeatures.length > 0) && (
              <Menu
                menuMaxContent
                anchorEl={anchorEl}
                key={workflowFormik?.values?.features?.length}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {filteredFeatures?.length > 0
                  ? filteredFeatures?.map((item: { uuid: string; name: string }) => (
                      <MenuItem
                        key={`${item.uuid}-${workflowFormik?.values?.features?.length}`}
                        value={item.name}
                        onClick={() => {
                          formik.setFieldValue(`ruleset.rules[${index}].feature`, item.name)
                          setAnchorEl(null)
                          // Resetting to rerender element to display correct data
                          // since key of input isnt the same as key of it's formik value
                          setTimeout(() => {
                            setAnchorEl(null)
                          }, 100)
                        }}
                      >
                        <Typography variant="a">{item.name}</Typography>
                      </MenuItem>
                    ))
                  : filteredFeatures?.length === 0 &&
                    formik?.values?.ruleset?.rules[index]?.feature && (
                      <MenuItem value={"new feature"}>
                        <Grid container item xs={12} justifyContent="space-between">
                          <Typography style={{ marginRight: "4px" }} variant="a">
                            {t("Condition.labels.addFeatureToSchema")}
                          </Typography>
                          <Select
                            hideDescription
                            id={"type"}
                            type="text"
                            value={type}
                            handleChange={(e) => setType(e?.target?.value.toString())}
                            fullWidth
                            options={[
                              t("Condition.options.number"),
                              t("Condition.options.text"),
                              t("Condition.options.boolean"),
                              t("Condition.options.undefined"),
                            ]}
                          />
                          <Button
                            style={{ marginTop: "5px" }}
                            onClick={() => {
                              const newFeat = {
                                name: formik?.values?.ruleset?.rules[index]?.feature?.replace(/\s/g, ""),
                                type,
                                is_required: true,
                                id: uuidv4(),
                                source: "workflow",
                                new: true,
                              }
                              workflowFormik?.setFieldValue("features", [
                                ...(workflowFormik?.values?.features ?? []),
                                newFeat,
                              ])

                              setAnchorEl(null)
                            }}
                            size="small"
                            variant="primary"
                          >
                            {t("Condition.labels.add")}
                          </Button>
                        </Grid>
                      </MenuItem>
                    )}
              </Menu>
            )}
          </Fragment>
        )}
      </Grid>

      <Grid item mt={readMode ? 1 : mode.create ? 0.6 : 0} xs={2.5}>
        <DataBlock value={t(`operators.${item.operator}`, { ns: "common", defaultValue: item.operator })} />
      </Grid>

      {item.operator === "between" ? (
        <Grid item xs={3.5} container>
          <Grid item xs md={4} mt={readMode ? 1 : 0}>
            {ruleInReadMode ? (
              <DataBlock value={formik?.values.ruleset.rules[index]?.value} />
            ) : (
              <InputText
                key={formik?.values.ruleset.rules[index]?.id}
                handleChange={formik?.handleChange}
                value={formik?.values.ruleset.rules[index]?.value}
                id={`ruleset.rules[${index}].value`}
                hideDescription
                disabled={disableRule}
                error={
                  touchedFormikRules &&
                  errorFormikRules &&
                  Array.isArray(touchedFormikRules) &&
                  touchedFormikRules[index]?.value &&
                  (errorFormikRules[index] as FormikErrors<Rule>)?.value &&
                  Boolean((errorFormikRules[index] as FormikErrors<Rule>)?.value) &&
                  (errorFormikRules[index] as FormikErrors<Rule>)?.value
                }
                placeholder={t("Condition.placeholders.value")}
                type="number"
                fullWidth
              />
            )}
          </Grid>

          <Grid item xs={0.5} mt={readMode ? 1 : 0} mx={0.7} className="and-block">
            <Typography variant="h3-bold" color="base">
              {t("Condition.labels.add")}
            </Typography>
          </Grid>

          <Grid item xs md={4} mt={readMode ? 1 : 0}>
            {ruleInReadMode ? (
              <DataBlock value={formik?.values.ruleset.rules[index]?.secondValue ?? ""} />
            ) : (
              <InputText
                type="number"
                hideDescription
                handleChange={formik?.handleChange}
                key={formik?.values.ruleset.rules[index]?.id}
                value={formik?.values.ruleset.rules[index]?.secondValue}
                id={`ruleset.rules[${index}].secondValue`}
                disabled={disableRule}
                placeholder={t("Condition.placeholders.value")}
                fullWidth
                error={
                  touchedFormikRules &&
                  errorFormikRules &&
                  Array.isArray(touchedFormikRules) &&
                  touchedFormikRules[index]?.secondValue &&
                  (errorFormikRules[index] as FormikErrors<Rule>)?.secondValue &&
                  Boolean((errorFormikRules[index] as FormikErrors<Rule>)?.secondValue) &&
                  (errorFormikRules[index] as FormikErrors<Rule>)?.secondValue
                }
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={3.5} mt={readMode ? 1 : 0}>
          {ruleInReadMode ? (
            <DataBlock value={formik?.values.ruleset.rules[index]?.value} />
          ) : (
            <InputText
              type="number"
              handleChange={formik?.handleChange}
              hideDescription
              key={formik?.values.ruleset.rules[index]?.id}
              value={formik?.values.ruleset.rules[index]?.value}
              id={`ruleset.rules[${index}].value`}
              disabled={disableRule}
              placeholder={t("Condition.placeholders.value")}
              fullWidth
              error={
                touchedFormikRules &&
                errorFormikRules &&
                Array.isArray(touchedFormikRules) &&
                touchedFormikRules[index]?.value &&
                (errorFormikRules[index] as FormikErrors<Rule>)?.value &&
                Boolean((errorFormikRules[index] as FormikErrors<Rule>)?.value) &&
                (errorFormikRules[index] as FormikErrors<Rule>)?.value
              }
            />
          )}
        </Grid>
      )}

      <Grid item display={"flex"} alignSelf={"self-end"}>
        <Typography variant="a">{"="}</Typography>
      </Grid>

      {/* Return label */}
      <Grid
        item
        // TODO:: fix when refactoring condition files
        md={item.operator?.toLowerCase() === "between" && (mode.create || mode.edit) ? 2.1 : 2.7}
        xs={2}
        mt={readMode ? 1 : 0}
      >
        {ruleInReadMode ? (
          <DataBlock value={formik?.values.ruleset.rules[index]?.returnValue} isLoading={isSingleLabelLoading} />
        ) : (
          <LabelsMenu
            filteredLabels={labels}
            value={formik?.values.ruleset.rules[index]?.returnValue ?? ""}
            onChange={(e: InputChangeEvent) => {
              formik.setFieldValue(`ruleset.rules.${[index]}.returnValue`, e.target.value)
              debouncedOnChange()
            }}
            onSelectingLabel={(item) => {
              formik.setFieldValue(`ruleset.rules.${[index]}.label_id`, item.uuid)
              formik.setFieldValue(`ruleset.rules.${[index]}.returnValue`, item.name)
            }}
            onAddingNewLabel={handleAddNewValue}
            disabled={disableRule}
            placeholder={t("Condition.placeholders.returnLabel")}
            onLoadMore={() => fetchNextPage()}
            hasMore={hasNextPage}
            isLoading={isLabelListLoading || isFetchingNextPage}
            error={
              touchedFormikRules &&
              errorFormikRules &&
              Array.isArray(touchedFormikRules) &&
              touchedFormikRules[index]?.returnValue &&
              (errorFormikRules[index] as FormikErrors<Rule>)?.label_id &&
              Boolean((errorFormikRules[index] as FormikErrors<Rule>)?.label_id) &&
              (errorFormikRules[index] as FormikErrors<Rule>)?.label_id
            }
          />
        )}
      </Grid>

      {/* Remove Rule Button*/}
      {item.operator?.toLowerCase() === "between" && (mode.create || mode.edit) && (
        <Grid item mt={mode.edit ? 2 : 1.5}>
          <Button
            variant="dangerous"
            disabled={disableRule}
            size="small"
            onClick={handleRemoveRule}
            tooltip={t("Condition.buttons.remove")}
          >
            <RemoveCircleOutlineIcon fontSize="inherit" />
          </Button>
        </Grid>
      )}
    </Grid>
  )
}
